import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  TrendingUp, 
  Euro, 
  AlertTriangle, 
  Users, 
  Activity, 
  ArrowUpRight, 
  Wallet,
  Clock,
  CheckCircle2,
  Calendar,
  Briefcase,
  Target
} from "lucide-react";
import { invokeFunctions } from "@/api/integrations";
import { Lead } from "@/api/entities";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

export default function CEODashboard() {
  // 1. Fetch Aggregated Metrics

  const { data: metrics, isLoading: loadingMetrics } = useQuery({
    queryKey: ['ceo-metrics'],
    queryFn: async () => {
      const resp = await invokeFunctions('getMetrics', {});
      return resp.data;
    },
    refetchInterval: 300000, // 5 min
  });

  // 2. Fetch Actual Leads for Lists
  const { data: leads, isLoading: loadingLeads } = useQuery({
    queryKey: ['ceo-leads'],
    queryFn: async () => {
      return await Lead.list();
    },
    refetchInterval: 300000,
  });

  if (loadingMetrics || loadingLeads) {
    return (
      <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
        <Skeleton className="h-10 w-64 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[400px] col-span-2 rounded-xl" />
          <Skeleton className="h-[400px] rounded-xl" />
        </div>
      </div>
    );
  }

  // Filter Data
  const topLeads = [...(leads || [])]
    .filter(l => (l.expected_value || 0) > 0 && !['cerrado', 'cerrado_ganado', 'cerrado_perdido'].includes(l.estado))
    .sort((a,b) => (b.expected_value || 0) - (a.expected_value || 0))
    .slice(0, 5);

  const riskLeads = (leads || []).filter(l => l.at_risk === true);
  
  const totalCerrados = (metrics?.porEstado?.cerrado || 0) + (metrics?.porEstado?.cerrado_ganado || 0) + (metrics?.porEstado?.cerrado_perdido || 0);

  return (
    <div className="p-6 space-y-8 max-w-[1600px] mx-auto pb-12">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3 tracking-tight">
            <div className="p-2 bg-indigo-600 rounded-xl shadow-lg shadow-indigo-500/20">
              <Activity className="w-8 h-8 text-white" />
            </div>
            CEO Executive Dashboard
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 font-medium">Visión estratégica de negocio y flujo de ingresos</p>
        </div>
        <div className="bg-white dark:bg-gray-900 shadow-sm px-4 py-2.5 rounded-xl text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-800 flex items-center gap-3 font-semibold">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
          {new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </div>
      </div>

      {/* SECCIÓN OBJETIVO MENSUAL */}
      <Card className="border-none shadow-xl bg-white dark:bg-gray-900 overflow-hidden ring-1 ring-indigo-100 dark:ring-indigo-900/50">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="space-y-2 text-center md:text-left min-w-[240px]">
              <h2 className="text-sm font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest flex items-center gap-2 justify-center md:justify-start">
                <Target className="w-4 h-4" /> Objetivo Mensual de Ventas
              </h2>
              <div className="flex items-baseline gap-2 justify-center md:justify-start">
                <span className="text-5xl font-black text-gray-900 dark:text-white tracking-tighter">
                  {(metrics?.monthly_target || 0).toLocaleString('es-ES')}€
                </span>
                <span className="text-sm font-bold text-gray-400 uppercase tracking-tighter">meta</span>
              </div>
            </div>
            
            <div className="flex-1 w-full space-y-4">
              <div className="flex justify-between text-[11px] font-black uppercase tracking-widest mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">Rendimiento:</span>
                  <span className="text-indigo-600 dark:text-indigo-400">{metrics?.progress || 0}% completado</span>
                </div>
                <span className={metrics?.gap > 0 ? "text-orange-500 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded" : "text-green-500 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded"}>
                  {metrics?.gap > 0 ? `Faltan ${metrics.gap.toLocaleString('es-ES')}€ para el hito` : "🎯 ¡Superado!"}
                </span>
              </div>
              <div className="relative h-6 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden shadow-inner border border-gray-200 dark:border-gray-700">
                <Progress value={metrics?.progress || 0} className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500 border-none transition-all duration-1000" />
              </div>
              <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                <span>0€ base</span>
                <div className="flex items-center gap-1.5 text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-2 py-0.5 rounded-full">
                  <TrendingUp className="w-3 h-3" />
                  <span>{(metrics?.forecastMes || 0).toLocaleString('es-ES')}€ acumulado</span>
                </div>
                <span>Final de mes</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 1. SECCIÓN DINERO & SALUD (Main KPIs) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard 
          title="Cartera Total (Forecast)" 
          value={`${(metrics?.forecastTotal || 0).toLocaleString('es-ES')}€`} 
          icon={Wallet} 
          color="indigo" 
          description="Suma total de valor esperado en el embudo"
        />
        <KPICard 
          title="Velocidad del Mes" 
          value={`${(metrics?.forecastMes || 0).toLocaleString('es-ES')}€`} 
          icon={TrendingUp} 
          color="fuchsia" 
          description="Nuevos ingresos potenciales este mes"
        />
        <KPICard 
          title="Nuevos Leads Hoy" 
          value={metrics?.leadsHoy || 0} 
          icon={Calendar} 
          color="green" 
          description="Actividad comercial en las últimas 24h"
        />
        <KPICard 
          title="Salud de Cartera" 
          value={`${((metrics?.altaPrioridad / (metrics?.totalLeads || 1)) * 100).toFixed(0)}%`}
          icon={Target} 
          color="blue" 
          description="% de leads marcados como alta prioridad"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* 2. TOP LEADS (High Impact) */}
        <Card className="lg:col-span-2 border-none shadow-xl bg-white dark:bg-gray-900 overflow-hidden ring-1 ring-gray-100 dark:ring-gray-800">
          <CardHeader className="bg-gradient-to-r from-gray-50 to-white dark:from-gray-800/80 dark:to-gray-900 border-b border-gray-100 dark:border-gray-800 flex flex-row items-center justify-between py-5">
            <CardTitle className="text-xl font-black text-gray-900 dark:text-white flex items-center gap-3">
              <ArrowUpRight className="w-6 h-6 text-fuchsia-500" />
              TOP 5: Oportunidades Críticas
            </CardTitle>
            <div className="px-3 py-1 bg-fuchsia-50 dark:bg-fuchsia-900/30 text-fuchsia-600 dark:text-fuchsia-400 text-[10px] font-black uppercase rounded-full">
               Alto Valor
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {topLeads.map(lead => (
                <div key={lead.id} className="p-5 hover:bg-gray-50/80 dark:hover:bg-gray-800/30 transition-all flex items-center justify-between group cursor-default">
                  <div className="space-y-1.5">
                    <p className="font-black text-lg text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">{lead.nombre}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-500 flex items-center gap-1.5">
                        <CheckCircle2 className="w-4 h-4 text-green-500" /> {lead.close_probability}% confianza
                      </span>
                      <span className="h-1 w-1 rounded-full bg-gray-300 dark:bg-gray-700"></span>
                      <span className="text-xs font-black text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/40 px-2 py-1 rounded-lg uppercase tracking-tight">
                        👉 {lead.next_action || 'Revisar'}
                      </span>
                    </div>
                    {lead.ai_reason && <p className="text-[10px] text-gray-400 italic mt-1 max-w-sm line-clamp-1">{lead.ai_reason}</p>}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-black text-gray-900 dark:text-white">
                      <span className="text-fuchsia-500 mr-1">💎</span>
                      {lead.expected_value.toLocaleString('es-ES')}€
                    </p>
                    <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mt-0.5">Valor Esperado</p>
                  </div>
                </div>
              ))}
              {topLeads.length === 0 && (
                <div className="p-16 text-center text-gray-500 font-medium">No hay leads con valoración económica activa.</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 3. EQUIPO & SALUD */}
        <div className="space-y-8">
          <Card className="border-none shadow-xl bg-white dark:bg-gray-900 overflow-hidden ring-1 ring-gray-100 dark:ring-gray-800">
            <CardHeader className="bg-gray-50/50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800">
              <CardTitle className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-blue-500" />
                Dinamismo del Equipo
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-5">
                {Object.entries(metrics?.porUsuario || {}).sort((a,b) => b[1] - a[1]).map(([user, count]) => (
                  <div key={user} className="flex items-center justify-between group">
                    <div className="flex items-center gap-3">
                       <div className="w-9 h-9 rounded-2xl bg-gradient-to-tr from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 flex items-center justify-center text-sm font-black shadow-sm group-hover:from-indigo-500 group-hover:to-indigo-600 group-hover:text-white transition-all duration-300">
                         {user.charAt(0).toUpperCase()}
                       </div>
                       <span className="text-sm font-bold text-gray-700 dark:text-gray-300 line-clamp-1">{user}</span>
                    </div>
                    <div className="flex flex-col items-end">
                      <span className="text-sm font-black text-gray-900 dark:text-white">{count}</span>
                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">Leads Activos</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-xl bg-gradient-to-br from-green-500 to-green-600 text-white overflow-hidden relative">
            <CardContent className="p-7">
              <div className="relative z-10">
                <h4 className="text-xs font-black text-green-100 mb-4 uppercase tracking-[0.2em] opacity-80">Objetivos Conseguidos</h4>
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-6xl font-black tracking-tighter">{totalCerrados}</p>
                    <p className="text-xs font-bold text-green-100 mt-2 opacity-90">Clientes que ya confían en nosotros</p>
                  </div>
                  <div className="bg-white/20 p-4 rounded-3xl backdrop-blur-md">
                    <CheckCircle2 className="w-10 h-10 text-white" />
                  </div>
                </div>
              </div>
              {/* Decorative design elements */}
              <div className="absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 bg-white/10 rounded-full blur-2xl"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 -ml-12 -mb-12 bg-black/10 rounded-full blur-xl"></div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* 4. LEADS EN RIESGO */}
      {riskLeads.length > 0 && (
        <Card className="border-none shadow-2xl bg-white dark:bg-gray-900 overflow-hidden ring-2 ring-red-500/20">
          <div className="bg-red-600 px-6 py-4 flex items-center justify-between">
            <CardTitle className="text-lg font-black text-white flex items-center gap-3">
              <AlertTriangle className="w-7 h-7 animate-bounce" />
              ALERTA DE RENEGOCIACIÓN: {riskLeads.length} Leads Críticos
            </CardTitle>
            <div className="px-4 py-1.5 bg-white/20 rounded-full text-[10px] font-black uppercase text-white backdrop-blur-sm border border-white/30">
               Atención CEO
            </div>
          </div>
          <CardContent className="p-6 bg-red-50/30 dark:bg-red-950/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {riskLeads.map(lead => (
                <div key={lead.id} className="bg-white dark:bg-gray-900 p-5 rounded-2xl shadow-sm border border-red-200 dark:border-red-900/50 flex flex-col gap-3 relative overflow-hidden group hover:border-red-500 transition-all duration-300">
                   <div className="absolute top-0 right-0 w-1 h-full bg-red-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                   <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center text-red-600 font-black text-xl">
                        !
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white group-hover:text-red-600 transition-colors uppercase tracking-tight">{lead.nombre}</p>
                        <p className="text-[10px] text-gray-500 font-bold">{lead.empresa || 'Empresa no especificada'}</p>
                      </div>
                   </div>
                   <div className="flex justify-between items-end mt-2">
                     <div className="space-y-1">
                        <p className="text-xs font-black text-red-600 uppercase tracking-tighter">Impacto en riesgo</p>
                        <p className="text-xl font-black text-gray-900 dark:text-white">💎 {lead.expected_value.toLocaleString('es-ES')}€</p>
                     </div>
                     <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 bg-gray-50 dark:bg-gray-800 px-2 py-1 rounded-lg">
                        <Clock className="w-3.5 h-3.5" /> 
                        {new Date(lead.updated_date).toLocaleDateString('es-ES', { month: 'short', day: 'numeric' })}
                     </div>
                   </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KPICard({ title, value, icon: Icon, color, description }) {
  const colorStyles = {
    indigo: "bg-indigo-600 text-white shadow-indigo-500/30",
    fuchsia: "bg-fuchsia-600 text-white shadow-fuchsia-500/30",
    green: "bg-green-600 text-white shadow-green-500/30",
    blue: "bg-blue-600 text-white shadow-blue-500/30",
  };

  const iconBaseStyles = {
    indigo: "bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400",
    fuchsia: "bg-fuchsia-50 dark:bg-fuchsia-950/40 text-fuchsia-600 dark:text-fuchsia-400",
    green: "bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400",
    blue: "bg-blue-50 dark:bg-blue-950/40 text-blue-600 dark:text-blue-400",
  };

  return (
    <Card className="border-none shadow-xl bg-white dark:bg-gray-900 overflow-hidden group hover:-translate-y-1 transition-all duration-500 ring-1 ring-gray-100 dark:ring-gray-800">
      <CardContent className="p-7">
        <div className="flex justify-between items-start mb-6">
          <div className={`p-4 rounded-2xl ${iconBaseStyles[color]} group-hover:scale-110 transition-transform duration-500`}>
            <Icon className="w-7 h-7" />
          </div>
          <div className="flex flex-col items-end">
            <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.25em]">Estándar</span>
            <div className="h-1 w-8 bg-gray-100 dark:bg-gray-800 mt-1 rounded-full overflow-hidden">
               <div className={`h-full w-2/3 ${color === 'indigo' ? 'bg-indigo-500' : color === 'fuchsia' ? 'bg-fuchsia-500' : color === 'green' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter font-mono">{value}</h3>
          <p className="text-xs font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest">{title}</p>
        </div>
        <div className="mt-6 pt-5 border-t border-gray-100 dark:border-gray-800">
          <p className="text-[10px] text-gray-400 dark:text-gray-500 font-bold italic leading-relaxed">
            {description}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
